import { useCallback, useContext, useEffect, useState } from "react";

import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import {
  updateGroupMemberPermissions,
  getGroupById,
  getGroupMember,
  getGroupMemberPermissions,
  GroupResource,
  UserResource,
  GroupMemberPermissions,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";

import { AuthStateContext } from "@/contexts/AuthContext";

const AlertGroupUserManagement = () => {
  const navigate = useNavigate();
  const { groupId, userId } = useParams();
  const { checkPermission } = useContext(AuthStateContext);
  const [user, setUser] = useState<UserResource | null>(null);
  const [group, setGroup] = useState<GroupResource | null>(null);
  const [permissions, setPermissions] = useState<GroupMemberPermissions>({
    bypass_moderation: false,
    moderate_bulletin_admittance: false,
    manage_members: false,
    manage_admins: false,
    flag_bulletin: false,
    manage_group: false,
    can_share_bulletin: false,
    can_revoke_bulletin: false,
    can_promote_content_moderators: false,
    can_transfer_bulletin_to_another_group: false,
    can_moderate_content: false,
  });
  const canManageUsers = checkPermission("manage", "users", Number(groupId));
  // only admins can manage admins and user cannot grant or revoke their own admin status
  const canManageAdmins = checkPermission("manage", "admins", Number(groupId));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch group, user and their permissions
  const fetchUserPermissions = useCallback(async () => {
    if (!groupId || !userId) return;

    setLoading(true);
    try {
      // Get group details
      const groupData = await getGroupById({
        path: { group_id: Number(groupId) },
      });
      setGroup(groupData.data ?? null);

      // Get user details
      const userData = await getGroupMember({
        path: { group_id: Number(groupId), user_id: userId },
      });
      setUser(userData.data ?? null);

      // Get user permissions
      const userPermissions = await getGroupMemberPermissions({
        path: { group_id: Number(groupId), user_id: userId },
      });
      setPermissions(
        userPermissions.data ?? {
          bypass_moderation: false,
          moderate_bulletin_admittance: false,
          manage_members: false,
          manage_admins: false,
          flag_bulletin: false,
          manage_group: false,
          can_share_bulletin: false,
          can_revoke_bulletin: false,
          can_promote_content_moderators: false,
          can_transfer_bulletin_to_another_group: false,
          can_moderate_content: false,
        },
      );
    } catch (error) {
      console.error("Error fetching user permissions", error);
      toast.error("Failed to load user permissions");
    } finally {
      setLoading(false);
    }
  }, [groupId, userId]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  const handleTogglePermission = async (
    permission: keyof GroupMemberPermissions,
  ) => {
    if (!groupId || !userId) return;

    const newValue = !permissions[permission];
    setPermissions((prev) => ({ ...prev, [permission]: newValue }));

    setSaving(true);
    try {
      await updateGroupMemberPermissions({
        path: { group_id: Number(groupId), user_id: userId },
        body: { [permission]: newValue },
      });
      toast.success("Saved");
    } catch (error) {
      setPermissions((prev) => ({ ...prev, [permission]: !newValue }));
      console.error("Error updating permissions", error);
      toast.error("Failed to update permission");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canManageUsers) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          You don&apos;t have permission to manage user permissions for this
          group.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() =>
            navigate(`/rubialert/manage-groups/${groupId}/members`)
          }
          sx={{ mt: 2 }}
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }} display="flex" alignItems="center">
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() =>
              navigate(`/rubialert/manage-groups/${groupId}/members`)
            }
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4">
              <b>{group?.name}</b> Group Member Permissions
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Typography gutterBottom>
                <strong>Name:</strong> {user?.first_name} {user?.last_name}
              </Typography>
              <Typography>
                <strong>Email:</strong> {user?.email}
              </Typography>
              <Typography>
                <strong>Role in Group:</strong> {user?.role}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Permissions in &quot;{group?.name}&quot;
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.bypass_moderation}
                        onChange={() =>
                          handleTogglePermission("bypass_moderation")
                        }
                        disabled={!canManageAdmins || saving}
                        color="primary"
                      />
                    }
                    label="Bypass Moderation"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    Bulletin shared by the user can bypass moderation
                    requirements for this group if the group has moderation
                    enabled
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.moderate_bulletin_admittance}
                        onChange={() =>
                          handleTogglePermission("moderate_bulletin_admittance")
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Moderate Bulletin Admittance"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can approve or reject bulletins shared to the group if
                    the group has moderation enabled
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.manage_members}
                        onChange={() =>
                          handleTogglePermission("manage_members")
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Manage Members"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can add to or remove members from the group
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.manage_admins}
                        onChange={() => handleTogglePermission("manage_admins")}
                        color="primary"
                        // only admins can manage admins
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Manage Permissions"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can manage each user&apos;s permissions within the
                    group
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.manage_group}
                        onChange={() => handleTogglePermission("manage_group")}
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Manage the Group"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can manage group information and bypass restrictions
                  </Typography>
                </Grid>

                {/* <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.flag_bulletin}
                        onChange={() => handleTogglePermission("flag_bulletin")}
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Flag Bulletin"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can flag bulletins as important
                  </Typography>
                </Grid> */}

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.can_share_bulletin}
                        onChange={() =>
                          handleTogglePermission("can_share_bulletin")
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Share Bulletin"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can share bulletins to other groups
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.can_revoke_bulletin}
                        onChange={() =>
                          handleTogglePermission("can_revoke_bulletin")
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Revoke Bulletin"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can revoke bulletins shared to the other groups from
                    this group
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.can_promote_content_moderators}
                        onChange={() =>
                          handleTogglePermission(
                            "can_promote_content_moderators",
                          )
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Promote Content Moderators"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can promote other members to content moderator role
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          permissions.can_transfer_bulletin_to_another_group
                        }
                        onChange={() =>
                          handleTogglePermission(
                            "can_transfer_bulletin_to_another_group",
                          )
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Transfer Bulletin to Another Group"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can transfer bulletins to another group
                  </Typography>
                </Grid>

                {/* <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={permissions.can_moderate_content}
                        onChange={() =>
                          handleTogglePermission("can_moderate_content")
                        }
                        color="primary"
                        disabled={!canManageAdmins || saving}
                      />
                    }
                    label="Moderate Content"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    User can modify bulletin detail which belongs to this group
                  </Typography>
                </Grid> */}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AlertGroupUserManagement;
